import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faUserMd, faUserNurse, faUserInjured, faCalendarAlt, faFileImport, faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { fetchPracticeSummary } from '../../store/slices/practiceSlice';
import EventLogSection from '../../components/EventLogSection/EventLogSection';
import '../PracticeManagement/PracticeDashboard/PracticeDashboard.css';
import './EventLogsPage.css';

const EventLogsPage = () => {
    const { moduleId, targetUserId } = useParams();
    const [searchParams] = useSearchParams();
    const practiceId = searchParams.get('practiceId');
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentPractice } = useSelector(state => state.practice ?? {});
    const { user } = useSelector(state => state.auth ?? {});

    const [practiceData, setPracticeData] = useState(() => {
        if (!currentPractice) return null;
        return String(currentPractice.id) === String(practiceId) ? currentPractice : null;
    });

    const tabs = [
        { id: 'practice', label: 'Practice', icon: faUserMd },
        { id: 'admin', label: 'Admin', icon: faUsers },
        { id: 'provider', label: 'Provider', icon: faUserMd },
        { id: 'caregiver', label: 'Caregiver', icon: faUserNurse },
        { id: 'patient', label: 'Patient', icon: faUserInjured },
        // System Caregiver tab disabled
        // { id: 'system-caregiver', label: 'System Caregiver', icon: faUserCheck },
        { id: 'schedule', label: 'Schedule & Target setting', icon: faCalendarAlt },
        { id: 'import', label: 'Import Patients', icon: faFileImport }
    ];

    useEffect(() => {
        const loadPracticeData = async () => {
            if (!practiceId) return;
            try {
                if (currentPractice && String(currentPractice.id) === String(practiceId)) {
                    setPracticeData(currentPractice);
                    return;
                }
                const result = await dispatch(fetchPracticeSummary(practiceId));
                if (fetchPracticeSummary.fulfilled.match(result)) {
                    setPracticeData(result.payload.data);
                }
            } catch (err) {
                console.error('Error fetching practice summary for logs:', err);
            }
        };
        loadPracticeData();
    }, [practiceId, dispatch, currentPractice]);

    const practiceLogoUrl = React.useMemo(() => {
        if (!practiceData?.practice_logo) return null;
        const raw = practiceData.practice_logo;
        if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        return `${API_URL.replace('/api', '')}${raw.startsWith('/') ? raw : `/${raw}`}`;
    }, [practiceData]);

    const [practiceLogoFailed, setPracticeLogoFailed] = useState(false);

    const formatBillingDate = (iso) => {
        if (!iso) return 'N/A';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return 'N/A';
        return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
    };

    const goBack = () => {
        navigate(-1);
    };

    const handleTabClick = (tabId) => {
        if (practiceId) {
            navigate(`/practices/${practiceId}?tab=${tabId}`);
        } else {
            goBack();
        }
    };

    const sectionFilter = searchParams.get('section');

    return (
        <div className="event-logs-page-container">
            {/* Practice Dashboard Header Context */}
            {practiceData && (
                <div className="practice-dashboard-header" style={{ marginBottom: '24px' }}>
                    <div className="practice-dashboard-header-top">
                        <div className="practice-dashboard-header-brand">
                            <h2 className="practice-dashboard-title">{practiceData.practice_name}</h2>
                        </div>
                        <div className="practice-dashboard-header-aside">
                            {practiceLogoUrl && !practiceLogoFailed && (
                                <div className="practice-dashboard-logo-wrap">
                                    <img
                                        src={practiceLogoUrl}
                                        alt="Practice Logo"
                                        className="practice-dashboard-logo-img"
                                        onError={() => setPracticeLogoFailed(true)}
                                    />
                                </div>
                            )}
                            <div className="practice-dashboard-billing">
                                <span className="practice-dashboard-billing-line">
                                    <span className="practice-dashboard-billing-prefix">Billing Period:</span>{' '}
                                    <span className="practice-dashboard-billing-dates">
                                        {formatBillingDate(practiceData.rpm_start_date)} - {formatBillingDate(practiceData.rpm_end_date)}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>

                    {user?.role_id !== 7 && (
                        <div className="practice-dashboard-nav-wrap">
                            <ul className="practice-dashboard-nav-tabs" role="tablist" style={{ marginBottom: 0 }}>
                                {tabs.map((tab) => {
                                    const isPseudoActive = moduleId?.toLowerCase().includes(tab.label.toLowerCase());
                                    return (
                                        <li key={tab.id} className="practice-dashboard-nav-item" role="presentation">
                                            <button
                                                className={`practice-dashboard-nav-link ${isPseudoActive ? 'active' : ''}`}
                                                type="button"
                                                onClick={() => handleTabClick(tab.id)}
                                            >
                                                <FontAwesomeIcon icon={tab.icon} className="practice-dashboard-me-2" />
                                                <span className="practice-dashboard-nav-label">{tab.label}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            <div className="event-logs-content">
                <EventLogSection 
                    moduleId={moduleId} 
                    targetUserId={targetUserId} 
                    practiceId={practiceId} 
                    sectionFilter={sectionFilter}
                />
            </div>
        </div>
    );
};

export default EventLogsPage;
